import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { canAccess, type Role } from '@/lib/auth'

interface ProtectedRouteProps {
  children: ReactNode
  /** Si se pasa, requiere este navId en NAV_BY_ROLE. Sino solo requiere sesión. */
  requireNav?: string
  /** Roles permitidos explícitos; si no se pasa y se da navId se deriva. */
  allowRoles?: ReadonlyArray<Role>
}

export function ProtectedRoute({ children, requireNav, allowRoles }: ProtectedRouteProps) {
  const { user, status } = useAuth()
  const location = useLocation()

  if (status === 'loading') {
    return (
      <div
        className="flex min-h-dvh items-center justify-center bg-slate-50 text-slate-500"
        role="status"
        aria-live="polite"
      >
        <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden />
        <span className="sr-only">Cargando sesión…</span>
      </div>
    )
  }

  if (status !== 'authenticated' || !user) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  if (requireNav && !canAccess(user.role, requireNav)) {
    return <UnauthorizedMessage role={user.role} />
  }

  if (allowRoles && !allowRoles.includes(user.role)) {
    return <UnauthorizedMessage role={user.role} />
  }

  return <>{children}</>
}

function UnauthorizedMessage({ role }: { role: Role }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-slate-50 px-6 text-center">
      <div className="rounded-full bg-[color:var(--color-fitlab-red)]/10 px-3 py-1 text-xs font-semibold text-[color:var(--color-fitlab-red-hover)]">
        Acceso restringido
      </div>
      <h1 className="text-xl font-semibold text-slate-900">Sin permisos para esta vista</h1>
      <p className="max-w-md text-sm text-slate-600">
        Tu rol actual ({role}) no tiene acceso a esta sección. Contacta a un administrador si crees
        que es un error.
      </p>
    </div>
  )
}
